import React, { useEffect, useState } from 'react';
import { Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Table, TableContainer, TableHead, TableRow, TableCell, TableBody, CircularProgress, Box, Stack, IconButton } from '@mui/material';
import axios from '../../api/axiosConfig';
import { Add, Edit, Delete } from '@mui/icons-material';
import PurchaseForm, { PurchaseFormValues } from './PurchaseForm';
import DividendForm, { DividendFormValues } from './DividendForm';
import { ApiResult } from '../../types/auth';
import { StockAnalysisDto as StockAnalysisType, PurchaseDto as Purchase, DividendDto as Dividend, InvestmentType, StockDto } from '../../types/stocks';
import { getStock } from '../../services/stocksApi';
import { formatCryptoQuantity, formatQuantity, formatEuro, formatNumber } from '../../utils/currency';
import { formatDate } from '../../utils/date';

interface StockDetailsDialogProps {
  open: boolean;
  stockId: string | null;
  onClose: () => void;
}

export default function StockDetailsDialog({ open, stockId, onClose }: StockDetailsDialogProps) {
  const [data, setData] = useState<StockAnalysisType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // A refused purchase change (e.g. the sales still need those shares) is shown above the tables
  const [actionError, setActionError] = useState('');
  const [purchaseFormOpen, setPurchaseFormOpen] = useState(false);
  const [editPurchase, setEditPurchase] = useState<Purchase | null>(null);
  const [dividendFormOpen, setDividendFormOpen] = useState(false);
  const [editDividend, setEditDividend] = useState<Dividend | null>(null);
  const [stock, setStock] = useState<StockDto | null>(null);

  useEffect(() => {
    if (!open || !stockId) return;
    setLoading(true);
    setError('');
    setActionError('');
    axios.get<ApiResult<StockAnalysisType>>(`/api/stock/${stockId}/analysis?includeDividends=true`)
      .then(res => {
        if (res.data.success && res.data.data) {
          setData(res.data.data);
        } else {
          setError(res.data.message || 'Não foi possível carregar a análise da ação');
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Não foi possível carregar os detalhes');
        setLoading(false);
      });
    // Also fetch base stock to know its investmentType for formatting
    (async () => {
      try {
        const s = await getStock(stockId);
        setStock(s);
      } catch (e) {
        // Non-fatal; fall back to non-crypto formatting
        setStock(null);
      }
    })();
  }, [open, stockId]);

  const handleAddPurchase = () => { setEditPurchase(null); setPurchaseFormOpen(true); };
  const handleEditPurchase = (p: Purchase) => { setEditPurchase(p); setPurchaseFormOpen(true); };
  const purchaseErrorMessage = (err: any) =>
    err?.response?.data?.message || 'Não foi possível guardar a alteração à compra.';
  const reloadAnalysis = async () => {
    const res = await axios.get<ApiResult<StockAnalysisType>>(`/api/stock/${stockId}/analysis?includeDividends=true`);
    if (res.data.success && res.data.data) {
      setData(res.data.data);
    }
  };
  const handleDeletePurchase = async (p: Purchase) => {
    if (!stockId) return;
    setActionError('');
    try {
      await axios.delete(`/api/stock/purchase/${p.publicId}`);
    } catch (err: any) {
      setActionError(purchaseErrorMessage(err));
      return;
    }
    setData(data => data ? { ...data, purchases: data.purchases.filter(x => x.publicId !== p.publicId) } : data);
  };
  const handlePurchaseFormSubmit = async (values: PurchaseFormValues) => {
    setActionError('');
    try {
      if (editPurchase) {
        await axios.put(`/api/stock/purchase/${editPurchase.publicId}`, values);
      } else if (stockId) {
        await axios.post(`/api/stock/${stockId}/purchase`, values);
      }
    } catch (err: any) {
      setActionError(purchaseErrorMessage(err));
    }
    setPurchaseFormOpen(false);
    setEditPurchase(null);
    await reloadAnalysis();
  };
  const handleAddDividend = () => { setEditDividend(null); setDividendFormOpen(true); };
  const handleEditDividend = (d: Dividend) => { setEditDividend(d); setDividendFormOpen(true); };
  const handleDeleteDividend = async (d: Dividend) => {
    if (!stockId) return;
    await axios.delete(`/api/stock/dividend/${d.publicId}`);
    setData(data => data ? { ...data, dividends: data.dividends.filter(x => x.publicId !== d.publicId) } : data);
  };
  const handleDividendFormSubmit = async (values: DividendFormValues) => {
    if (editDividend) {
      await axios.put(`/api/stock/dividend/${editDividend.publicId}`, values);
    } else if (stockId) {
      await axios.post(`/api/stock/${stockId}/dividend`, values);
    }
    setDividendFormOpen(false);
    setEditDividend(null);
    // reload
    const res = await axios.get<ApiResult<StockAnalysisType>>(`/api/stock/${stockId}/analysis?includeDividends=true`);
    if (res.data.success && res.data.data) {
      setData(res.data.data);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Detalhes da ação</DialogTitle>
      <DialogContent>
        {loading ? <CircularProgress /> : error ? <Typography color="error">{error}</Typography> : data && (
          <Box>
            {actionError && <Alert severity="error" onClose={() => setActionError('')} sx={{ mb: 2 }}>{actionError}</Alert>}
            <Typography variant="h6">Símbolo: {data.symbol}</Typography>
            <Stack direction="row" spacing={4} useFlexGap flexWrap="wrap" my={2}>
              <Typography>Total investido: {formatEuro(data.totalInvested)}</Typography>
              <Typography>Valor atual: {formatEuro(data.currentValue)}</Typography>
              <Typography>Total de dividendos: {formatEuro(data.totalDividends)}</Typography>
              <Typography>Lucro/Prejuízo: {formatEuro(data.profitLoss)}</Typography>
            </Stack>
            <Typography variant="subtitle1">Purchases
              <IconButton size="small" onClick={handleAddPurchase}><Add fontSize="small" /></IconButton>
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Quantidade</TableCell>
                    <TableCell>Preço/unidade</TableCell>
                    <TableCell>Câmbio</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.purchases.map(p => (
                    <TableRow key={p.publicId}>
                      <TableCell>{formatDate(p.date)}</TableCell>
                      <TableCell>
                        {(stock?.investmentType === InvestmentType.Cryptocurrency)
                          ? formatCryptoQuantity(p.quantity)
                          : formatQuantity(p.quantity)}
                      </TableCell>
                      <TableCell>
                        {(stock?.investmentType === InvestmentType.Cryptocurrency)
                          ? formatCryptoQuantity(p.pricePerShare)
                          : formatQuantity(p.pricePerShare)}
                      </TableCell>
                      <TableCell>{p.cambio != null ? formatNumber(p.cambio, 6) : ''}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditPurchase(p)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeletePurchase(p)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <PurchaseForm
              open={purchaseFormOpen}
              initialValues={editPurchase ? {
                // Normalize to yyyy-MM-dd for HTML date input
                date: (typeof editPurchase.date === 'string' ? editPurchase.date.substring(0, 10) : new Date(editPurchase.date as any).toISOString().substring(0,10)),
                quantity: editPurchase.quantity,
                pricePerShare: editPurchase.pricePerShare,
                cambio: editPurchase.cambio ?? ''
              } : undefined}
              onClose={() => { setPurchaseFormOpen(false); setEditPurchase(null); }}
              onSubmit={handlePurchaseFormSubmit}
            />
            <Typography variant="subtitle1" mt={2}>Dividends
              <IconButton size="small" onClick={handleAddDividend}><Add fontSize="small" /></IconButton>
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Bruto</TableCell>
                    <TableCell>Imposto pago</TableCell>
                    <TableCell>Valor</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.dividends.map(d => (
                    <TableRow key={d.publicId}>
                      <TableCell>{formatDate(d.date)}</TableCell>
                      <TableCell>{formatEuro(d.grossAmount)}</TableCell>
                      <TableCell>{formatEuro(d.taxPaid)}</TableCell>
                      <TableCell>{formatEuro(d.amount)}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => handleEditDividend(d)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => handleDeleteDividend(d)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <DividendForm
              open={dividendFormOpen}
              initialValues={editDividend ? {
                date: editDividend.date,
                grossAmount: editDividend.grossAmount,
                taxPaid: editDividend.taxPaid,
                amount: editDividend.amount
              } : undefined}
              onClose={() => { setDividendFormOpen(false); setEditDividend(null); }}
              onSubmit={handleDividendFormSubmit}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
}
